import { ParameterValues } from "calculation/parameter";
import { ReactElement, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { exportParametersCsv } from "reports/reports";
import { URL } from "myconstants";
import { Action, State } from "../../state/state";
import { ParametersTable } from "components/zoning-plan-common/ParametersTable";
import { DownloadButton } from "components/common/DownloadButton";
import { NoData } from "components/common/NoData";
import { Button } from "components/common/Button";
import { useState } from "react";
import { StatusBar } from "components/common/StatusBar";
import moment from "moment";

export function Parameters(props: {
  dispatch: (action: Action) => void;
  state: State;
}): ReactElement {
  const { dispatch, state } = props;
  const [showStatusBar, setShowStatusBar] = useState<boolean>(false);
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);
  const [parameterValues, setParameterValues] = useState<ParameterValues>();

  function toggleReadonlyParameterSet(e: any) {
    if (state.zoningPlanLegal.parameterSet !== undefined) {
      state.zoningPlanLegal.parameterSet.readonly =
        !state.zoningPlanLegal.parameterSet.readonly;
      saveSelectedParameterSet();
    }
  }

  async function saveSelectedParameterSet() {
    if (state.zoningPlanLegal.parameterSet !== undefined) {
      state.zoningPlanLegal.parameterSet.modifiedAt = moment().format();
      try {
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/parametersets/${state.zoningPlanLegal.parameterSet.id}`,
          {
            headers: {
              "Content-Type": "application/json",
            },
            method: "PUT",
            body: JSON.stringify(state.zoningPlanLegal.parameterSet),
            credentials: "include",
          }
        );

        dispatch({
          type: "saveParameterSet",
          data: { parameterSet: state.zoningPlanLegal.parameterSet },
        });
        setShowStatusBar(true);
        setTimeout(() => setShowStatusBar(false), 1500);
        setHasPendingChanges(false);
      } catch {
        history.replace(URL.error);
      }
    }
  }

  const history = useHistory();

  useEffect(() => {
    if (state.zoningPlanLegal.parameterSet !== undefined) {
      setParameterValues(
        new ParameterValues(state.zoningPlanLegal.parameterSet.parameterValues)
      );
    }
  }, [state.zoningPlanLegal.parameterSet]);

  const hasData =
    parameterValues !== undefined &&
    state.parameters !== undefined &&
    state.zoningPlanLegal.scenario !== undefined &&
    state.zoningPlanLegal.parameterSet !== undefined;

  return (
    <div className="flex flex-col text-gray-700 w-full h-">
      <StatusBar show={showStatusBar} text="Gespeichert" />
      {!hasData && <NoData />}
      {hasData && (
        <div className="flex flex-col space-y-4">
          <div className="mb-4">
            <h1 className="text-lg font-bold mb-2">Kennwerte</h1>
            <p className="my-2">
              Für den rechtskräftigen Zonenplan gelten folgende Kennwerte. Diese
              können in Absprache mit der Dienststelle Raum und Wirtschaft
              einmalig angepasst werden.
            </p>
          </div>
          <ParametersTable
            parameters={state.parameters!}
            parameterValues={parameterValues!}
            scenario={state.zoningPlanLegal.scenario!}
            readonlyParameterSet={state.zoningPlanLegal.parameterSet!.readonly}
            onChange={() => setHasPendingChanges(true)}
          />
          {!hasPendingChanges && <div className="h-6">&nbsp;</div>}
          {hasPendingChanges && (
            <span className="h-6 text-red-400 font-bold">
              Sie haben ungespeicherte Änderungen!
            </span>
          )}
          <div className="flex flex-row items-center space-x-4 mt-4">
            <Button
              className={`${hasPendingChanges ? "bg-blue-400" : "bg-blue-200"}`}
              onClick={() => {
                state.zoningPlanLegal.parameterSet!.parameterValues =
                  parameterValues!.createParameterValueDtos();
                saveSelectedParameterSet();
              }}
              disabled={hasPendingChanges ? false : true}
            >
              Speichern
            </Button>
            <Button
              className={`${hasPendingChanges ? "bg-blue-400" : "bg-blue-200"}`}
              onClick={() => {
                if (state.zoningPlanLegal.parameterSet !== undefined) {
                  setParameterValues(
                    new ParameterValues(
                      state.zoningPlanLegal.parameterSet.parameterValues
                    )
                  );
                  setHasPendingChanges(false);
                }
              }}
              disabled={hasPendingChanges ? false : true}
            >
              Rückgängig
            </Button>
            {state.user.user?.isAdmin && (
              <div className="flex flex-row items-center space-x-2">
                <input
                  className="w-4 h-4 text-blue-400 bg-gray-100 rounded border-gray-300 focus:ring-0"
                  type="checkbox"
                  id="freeze"
                  name="freeze"
                  onChange={toggleReadonlyParameterSet}
                  checked={state.zoningPlanLegal.parameterSet?.readonly}
                />
                <span>Bearbeitung sperren</span>
              </div>
            )}
          </div>
          <div className="h-full"></div>
          <div className="mt-4 flex flex-col space-y-2">
            <h2 className="text-md font-bold">Downloads</h2>
            <DownloadButton
              label="CSV"
              text="Kennwerte im Detail"
              width="w-52"
              onClick={() =>
                exportParametersCsv(
                  state.parameters!,
                  parameterValues!,
                  state.zoningPlanLegal.scenario!,
                  state.zoningPlanLegal.parameterSet!.name,
                  state.selectedMunicipality!.gemeinde
                )
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
